/**
 * Site Analyses — service layer for the Site Analysis mapping tool.
 *
 * Backed by api/site-analyses.js (Supabase `site_analyses` + the public `site-analyses`
 * bucket). Round 27: exactly ONE analysis per PROJECT — created lazily on first open, never
 * deleted, never switched between. The document is a single JSON file (backgrounds + typed kit
 * items + north/latitude/scale) uploaded via signed-URL PUT — it's multi-MB, so it never passes
 * through the Vercel function body.
 */

async function authHeaders() {
  const token = await window.__clerkGetToken()
  return { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token }
}

async function call(method, body, qs) {
  const res = await fetch('/api/site-analyses' + (qs ? '?' + qs : ''), {
    method,
    headers: await authHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error || ('Site analysis request failed (' + res.status + ')'))
  return data
}

/**
 * Get the project's one analysis, creating it if this is the first time it's been opened.
 * Returns metadata + the parsed document: { id, name, updatedAt, doc }.
 */
export async function getOrCreateSiteAnalysis(projectId, msId) {
  const qs = 'projectId=' + encodeURIComponent(projectId) + (msId ? '&msId=' + encodeURIComponent(msId) : '')
  const { analysis } = await call('GET', null, qs)
  // Cache-bust — the bucket is public/CDN-cached and autosave rewrites the same path.
  const res = await fetch(analysis.fileUrl + '?t=' + Date.now())
  if (!res.ok) throw new Error('Could not load the analysis file (' + res.status + ')')
  const doc = await res.json()
  return { ...analysis, doc }
}

async function putSigned(signedUrl, body, contentType) {
  const res = await fetch(signedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType, 'x-upsert': 'true' },
    body,
  })
  if (!res.ok) throw new Error('Upload failed (' + res.status + ')')
}

/**
 * Save (create or overwrite) the project's analysis. `analysisId` null = create (projectId
 * required) — in practice this only happens if `getOrCreateSiteAnalysis` hasn't run yet, since
 * that already creates the row on first open. `pdfBlob` (optional) is the "final state" PDF
 * (SiteAnalysisModal.jsx#buildPdfBlob) — stored alongside the JSON so `freezeSiteAnalysis` has
 * something to publish when a milestone posts. Returns the id either way.
 */
export async function saveSiteAnalysis({ analysisId, msId, projectId, name, jsonBlob, thumbBlob, pdfBlob }) {
  const { analysisId: id, fileUpload, thumbUpload, pdfUpload } = await call('POST', {
    action: 'saveUrl', analysisId: analysisId || undefined, msId, projectId, name,
  })
  await putSigned(fileUpload.signedUrl, jsonBlob, 'application/json')
  if (thumbBlob) await putSigned(thumbUpload.signedUrl, thumbBlob, 'image/png')
  if (pdfBlob && pdfUpload) await putSigned(pdfUpload.signedUrl, pdfBlob, 'application/pdf')
  await call('POST', { action: 'confirm', analysisId: id, name })
  return id
}

/**
 * Publish the project's analysis PDF into the posted milestone's Documents (source
 * 'site-analysis') — called on every milestone post, best-effort. → { ok, frozen, url? }
 */
export function freezeSiteAnalysis(projectId, msId) {
  return call('POST', { action: 'freeze', projectId, msId })
}

/**
 * SOLAR STUDY round (2026-08-22): the project's CALIBRATED location from its Site Analysis
 * map, or null. Site Analysis's lat/lng is the human-corrected value (its satellite underlay
 * is drag-nudgeable, so someone has usually verified the pin), which makes it the preferred
 * source of truth over the Modeler's own raw, uncorrected geocode — the first concrete linkage
 * between the two tools' site data. NON-CREATING (`peek=1`): asking never spawns a Site
 * Analysis row for a project that never opened that tool, and the multi-MB analysis document
 * is read server-side — only the two numbers come back. → { latitude, longitude } | null
 */
export async function getSiteAnalysisGeoAnchor(projectId) {
  const data = await call('GET', null, 'projectId=' + encodeURIComponent(projectId) + '&peek=1')
  return data.found ? { latitude: data.latitude, longitude: data.longitude } : null
}
